<template>
  <Container group-name="ne-lv-columns" :get-child-payload="getPayload" @drop="onDrop">
    <Draggable v-for="(column, columnIndex) in currentRow.columns" :key="columnIndex" class="ne-lv-single-object">
      <section class="cursor-pointer" :class="{ active: activeObject && activeObject.id === column.id }">
        <div v-if="column.elements.length" class="ne-lv-toggle" @click="toggleStatus(column.id)">
          <i :class="!listViewStatus[column.id] ? angleDownIcon : angleUpIcon" />
        </div>
        <div class="ne-lv-text" @click="clickObject(column.id)">
          <span>Column:</span>
          <label class="custom-ellipsis">{{ options2[column.id].ElementTitle }}</label>
        </div>
        <div class="ne-lv-buttons ml-2">
          <b-button v-if="options2[column.id].show" variant="primary" size="sm" title="Show / Hide" @click="clickToggleView(column.id)">
            <i :class="eyeIcon" />
          </b-button>
          <b-button v-else variant="danger" size="sm" title="Show / Hide" @click="clickToggleView(column.id)">
            <i :class="eyeSlashIcon" />
          </b-button>
          <b-button v-if="!options2[column.id].fromLanding" variant="outline-success" size="sm" title="Copy" class="ml-1" @click="clickCopy(column.id)">
            <i :class="cloneIcon" />
          </b-button>
          <b-button v-if="!options2[column.id].fromLanding" variant="outline-danger" size="sm" title="Delete" class="ml-1" @click="clickDelete(column.id)">
            <i :class="deleteIcon" />
          </b-button>
        </div>
      </section>
      <div v-if="listViewStatus[column.id] || !column.elements.length" class="ne-lv-nested">
        <div class="position-relative">
          <ListElementView
            :sections2="sections2"
            :options2="options2"
            :current-section="currentSection"
            :current-row="currentRow"
            :current-column="column"
            @callEvent="callEvent"
          />
          <div v-if="!column.elements.length" class="ne-lv-comment">
            <p>No Element added here</p>
          </div>
        </div>
      </div>
    </Draggable>
  </Container>
</template>

<script>
import { findIndex } from '@src/helper'
import { Container, Draggable } from 'vue-smooth-dnd'
import ListElementView from './ListElementView.vue'
import {
  ANGLEDOWN_ICON,
  ANGLEUP_ICON,
  CLONE_ICON,
  DELETE_ICON,
  EYESLASH_ICON,
  EYE_ICON
} from '@src/icons'
export default {
  components: {
    Container,
    Draggable,
    ListElementView
  },
  props: {
    sections2: { type: Array, default: null, required: true },
    options2: { type: [Object], default: null, required: true },
    currentSection: { type: [Object], default: null, required: true },
    currentRow: { type: [Object], default: null, required: true }
  },
  data() {
    return {
      angleDownIcon: ANGLEDOWN_ICON,
      angleUpIcon: ANGLEUP_ICON,
      cloneIcon: CLONE_ICON,
      deleteIcon: DELETE_ICON,
      eyeIcon: EYE_ICON,
      eyeSlashIcon: EYESLASH_ICON
    }
  },
  computed: {
    activeObject() {
      return this.$store.getters['utility/activeObject']
    },
    listViewStatus() {
      return this.$store.getters['utility/listViewStatus']
    }
  },
  methods: {
    callEvent(params) {
      this.$emit('callEvent', params)
    },
    findSectionIndex(id) {
      return findIndex(this.sections2, 'id', id)
    },
    findRowIndex(sectionId, id) {
      let index = -1
      const sectionIndex = this.findSectionIndex(sectionId)
      if (sectionIndex >= 0) {
        index = findIndex(this.sections2[sectionIndex].rows, 'id', id)
      }
      return [sectionIndex, index]
    },
    getPayload(index) {
      const indexes = this.findRowIndex(this.currentSection.id, this.currentRow.id)
      if (indexes[0] >= 0 && indexes[1] >= 0) {
        const sectionIndex = indexes[0]
        const rowIndex = indexes[1]
        const column = this.sections2[sectionIndex].rows[rowIndex].columns[index]
        return {
          ...column,
          source: 'ne-lv-columns'
        }
      }
      return null
    },
    onDrop(dragResult) {
      if (!dragResult) return
      const { removedIndex, addedIndex, payload } = dragResult
      const sectionId = this.currentSection.id
      const rowId = this.currentRow.id
      if (!payload || !payload.id) return

      const newColumn = JSON.parse(JSON.stringify(payload))

      if (newColumn.source === 'ne-lv-columns') {
        const indexes = this.findRowIndex(sectionId, rowId)
        if (indexes[0] < 0 || indexes[1] < 0) return

        const sectionIndex = indexes[0]
        const rowIndex = indexes[1]

        if (removedIndex !== null && removedIndex >= 0) {
          this.sections2[sectionIndex].rows[rowIndex].columns.splice(removedIndex, 1)
        }
        if (addedIndex !== null && addedIndex >= 0) {
          this.sections2[sectionIndex].rows[rowIndex].columns.splice(addedIndex, 0, newColumn)
        }
      }
    },
    toggleStatus(objectId) {
      this.callEvent({
        methodName: 'setListViewStatus',
        eventData: {
          ...this.listViewStatus,
          [objectId]: !this.listViewStatus[objectId]
        },
        target: 'ListView'
      })
    },
    clickObject(columnId) {
      this.callEvent({
        methodName: 'setActiveObject',
        eventData: { id: columnId, rowId: this.currentRow.id, sectionId: this.currentSection.id, type: 'column' },
        target: 'Design'
      })
      this.callEvent({ methodName: 'setDefaultEditorModal', target: 'Design' })
      const elementDOM = document.getElementById(columnId)
      if (elementDOM) elementDOM.scrollIntoView()
    },
    clickToggleView(columnId) {
      const options2 = JSON.parse(JSON.stringify(this.options2))
      options2[columnId].show = !options2[columnId].show
      this.callEvent({ methodName: 'setOptions2', eventData: options2, target: 'Design' })
    },
    clickCopy(columnId) {
      this.callEvent({
        methodName: 'copyColumn',
        eventData: { sectionId: this.currentSection.id, rowId: this.currentRow.id, columnId }
      })
    },
    clickDelete(columnId) {
      this.callEvent({
        methodName: 'deleteColumn',
        eventData: { sectionId: this.currentSection.id, rowId: this.currentRow.id, columnId }
      })
    }
  }
}
</script>